import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useAxios from "@hooks/use-axios";
import { toastify } from "@helpers/toast";
import { smtpDefaults, smtpSchema } from "./schema";

const useBrandSmtp = (brandId) => {
    const { axiosInstance } = useAxios({ base: "brand", showAlert: false });
    const [step, setStep] = useState(1);
    const [done, setDone] = useState([]);
    const [submitted, setSubmitted] = useState(false);

    const methods = useForm({
        resolver     : yupResolver(smtpSchema),
        defaultValues: smtpDefaults
    });
    const { control, watch, setValue, reset, trigger, handleSubmit, formState: { errors } } = methods;
    const option = watch("option");
    const oauthConnected = watch("oauth_connected");

    useEffect(() => {
        const fetchSmtp = async () => {
            try {
                const res = await axiosInstance.get(`v1/accounts/${brandId}/smtp`);
                const result = res.data || res;
                const config = result.data || result;
                if (config?.status !== "configured") return;

                reset({
                    ...smtpDefaults,
                    option    : config.provider || "other",
                    server    : config.host || "",
                    port      : String(config.port ?? smtpDefaults.port),
                    username  : config.username || "",
                    password  : "",
                    security  : config.security_type || smtpDefaults.security,
                    from_name : config.from_name || "",
                    email     : config.from_email || "",
                    rate_limit: config.rate_limit ?? "",
                    notify    : (config.notify_emails || []).join(", ")
                });
                setDone([1, 2]);
                setSubmitted(true);
            } catch {
                // No SMTP configured yet — keep the form at its defaults.
            }
        };
        fetchSmtp();
    }, [brandId]); // eslint-disable-line react-hooks/exhaustive-deps

    const connectOauth = () => {
        setValue("oauth_connected", true);
        toastify({ title: option === "gmail" ? "Google account authorised" : "Outlook account authorised", type: "success" });
    };

    const goToStep = (target) => {
        if (target === 2 && !done.includes(1)) return;
        if (target === 3 && !done.includes(2)) return;
        setStep(target);
    };

    const next = async () => {
        const step2Fields = option === "other"
            ? ["server", "username", "from_name", "email", "port", "password"]
            : ["from_name", "oauth_connected"];
        const fieldsToValidate = step === 1 ? ["option"] : step2Fields;

        const isValid = await trigger(fieldsToValidate);
        if (!isValid) {
            if (step === 2) toastify({ title: "Complete the required server configuration fields", type: "error" });
            return;
        }
        setDone((prev) => (prev.includes(step) ? prev : [...prev, step]));
        setStep(step + 1);
    };

    const previous = () => setStep((prev) => Math.max(1, prev - 1));

    const submit = handleSubmit(async (data) => {
        await axiosInstance.put(`v1/accounts/${brandId}/smtp`, {
            host         : data.server,
            port         : Number(data.port),
            username     : data.username,
            password     : data.password,
            security_type: data.security,
            from_name    : data.from_name,
            from_email   : data.email
        });
        setDone((prev) => (prev.includes(3) ? prev : [...prev, 3]));
        setSubmitted(true);
        toastify({ title: "SMTP configuration saved", type: "success" });
    });

    return {
        control,
        errors,
        option,
        oauthConnected,
        step,
        done,
        submitted,
        setValue,
        connectOauth,
        goToStep,
        next,
        previous,
        submit
    };
};

export default useBrandSmtp;
